import React, { useEffect, useState } from 'react';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
  /**
   * Si es true (default), backdrop y Escape muestran confirmación antes de cerrar.
   * Pasar false en modales informativos sin formularios.
   */
  confirmOnExit?: boolean;
}

export const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  footer,
  confirmOnExit = true,
}) => {
  const [showConfirm, setShowConfirm] = useState(false);

  // Resetear confirmación cuando el modal se abre/cierra
  useEffect(() => {
    if (!isOpen) setShowConfirm(false);
  }, [isOpen]);

  /** Cierre desde el backdrop o Escape → siempre pide confirmación si confirmOnExit */
  const handleBackdropOrEsc = () => {
    if (confirmOnExit) {
      setShowConfirm(true);
    } else {
      onClose();
    }
  };

  // Tecla Escape
  useEffect(() => {
    if (!isOpen) return;
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (showConfirm) {
          setShowConfirm(false); // Escape descarta el banner de confirmación
        } else {
          handleBackdropOrEsc();
        }
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, showConfirm, confirmOnExit]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity backdrop-blur-sm"
        onClick={handleBackdropOrEsc}
      />

      {/* Content */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative transform rounded-xl bg-white text-left shadow-2xl transition-all w-full max-w-[calc(100%-1rem)] sm:max-w-2xl lg:max-w-5xl max-h-[95vh] sm:max-h-[90vh] flex flex-col">

          {/* Header */}
          <div className="border-b border-gray-100 bg-white px-4 py-3 sm:px-6 flex justify-between items-center flex-shrink-0 sticky top-0 z-10">
            <h3 className="text-lg font-semibold leading-6 text-gray-900">{title}</h3>
            <button
              onClick={onClose}
              className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none"
              aria-label="Cerrar"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Body */}
          <div className="px-4 py-5 sm:p-6 overflow-y-auto flex-1">
            {children}
          </div>

          {/* Footer opcional */}
          {footer && (
            <div className="border-t border-gray-100 bg-white px-4 py-3 sm:px-6 flex-shrink-0">
              {footer}
            </div>
          )}

          {/* Banner de confirmación */}
          {showConfirm && (
            <div className="absolute inset-x-0 bottom-0 z-20 rounded-b-xl bg-amber-50 border-t-2 border-amber-400 px-5 py-4 shadow-xl flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
              <div className="flex items-start gap-3">
                <svg className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                </svg>
                <div>
                  <p className="text-sm font-semibold text-amber-900">¿Cerrar sin guardar?</p>
                  <p className="text-xs text-amber-700 mt-0.5">Los datos que no hayas guardado se perderán.</p>
                </div>
              </div>
              <div className="flex gap-2 flex-shrink-0 w-full sm:w-auto">
                <button
                  onClick={() => setShowConfirm(false)}
                  className="flex-1 sm:flex-none px-4 py-2 text-sm font-medium rounded-lg border border-amber-400 text-amber-800 bg-white hover:bg-amber-50 transition-colors"
                >
                  Seguir editando
                </button>
                <button
                  onClick={onClose}
                  className="flex-1 sm:flex-none px-4 py-2 text-sm font-medium rounded-lg bg-amber-500 text-white hover:bg-amber-600 transition-colors"
                >
                  Cerrar sin guardar
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
